import { translate } from '@jsverse/transloco';
import {
  createEffect,
  dispatch,
  ofType,
  registerEffects,
  removeEffects,
  tapResult,
} from '@ngneat/effects';
import { getCurrentAuthenticatedUser } from '@tmx/auth';
import { subscribeOnce } from '@tmx/core';
import {
  AccountsApi,
  CopilotMessagePublisherApi,
  CpihMessage,
  CpihMessageExporterApi,
  FleetApi,
  FleetVehicleInformation,
  NormalizedResponse,
  SmsNotificationServiceApi,
  UserPagedCollectionParams,
} from '@tmx/data-access';
import {
  ClientOnlyValue,
  LoadingRowsDescriptor,
  RowDescriptor,
  SelectAllState,
  UpdateRowsDescriptor,
} from '@tmx/operandi';
import {
  catchError,
  combineLatest,
  filter,
  map,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';

import { lookupAccountCloudProvider } from '../../features/diagnostics/repositories/diagnostics.repository';
import { AssetsImportResponse } from '../../models/assets-import-response';
import { TEN_SECONDS_IN_MS } from '../../models/constants';
import { EMPTY_ROWS_DESCRIPTOR } from '../../models/empty-rows-descriptor';
import { HistoricalNotification } from '../../models/historical-notification';
import { LicenseProduct } from '../../models/license-product';
import { LicenseUser } from '../../models/license-user';
import { ProductInvite } from '../../models/product-invite';
import { createDriverIdLookup } from '../../utils/create-driver-id-lookup';
import { determineMainDriver } from '../../utils/determine-main-driver';
import { downloadFile } from '../../utils/download-file';
import { generateCopilotQuickLoginLink } from '../../utils/generate-copilot-quick-login-link';
import { getErrorContext } from '../../utils/get-error-context';
import { isCoPilotLicense } from '../../utils/is-copilot-license';
import { reportToTrackjs } from '../../utils/report-to-trackjs';
import { setFilterOptions } from '../../utils/set-filter-options';
import { setBrazeAttribute } from '../ams/ams.actions';
import { accountSummarySnapshot } from '../ams/ams.repository';
import {
  companySettingsSnapshot,
  dataTablePageSizeSnapshot,
  linkedDataEnabled,
  partnerSettingsSnapshot,
} from '../app-settings/app-settings.repository';
import { isFeatureEnabled } from '../feature-flags/feature-flags.repository';
import { updateToast } from '../toasts.repository';
import { chimportAssets } from '../utils/chimport-assets';
import { createLicenseUsers } from '../utils/create-license-users';
import { mapSmsStatusesToAssets } from '../utils/map-sms-statuses-to-assets';
import {
  checkIfAssetIsChanged,
  checkIfAssetIsCreated,
  checkIfAssetIsDeleted,
  clearAssets,
  clearImportResponse,
  clearJobTraceId,
  clearSelectedAssets,
  clearSingleAssetDriverData,
  importAssets,
  inviteSamsaraUser,
  inviteSamsaraUsers,
  loadAssets,
  loadCopilotVersions,
  loadFleetData,
  loadHistoricalNotifications,
  loadMapDataVersions,
  loadSingleAssetDriverData,
  loadUsersCsv,
  refreshAssetsRowsDescriptor,
  reloadAssets,
  selectAllAssets,
  selectAllBulkAssign,
  selectAllBulkDelete,
  selectAllBulkInvite,
  selectAllBulkNotify,
  selectAllBulkResetPassword,
  selectAllBulkUnassign,
  selectAsset,
  setAssetsOffset,
  setAssetsQuery,
  setAssetsSort,
  setFilterAssetsSelectOption,
  setFilterByAssetIds,
} from './assets.actions';
import {
  assetStoreEntities,
  assetStoreSnapshot,
  clearAssetsStore,
  fleetInformationStatus$,
  linkedDataStatus$,
  loadingSnapshot,
  smsInformationStatus$,
  trackAssetsRequests,
  trackAssetsRequestsStatus,
  updateAssetCreatedOrUpdated,
  updateAssetDeleted,
  updateAssetImportStatus,
  updateAssets,
  updateAssetsRowsDescriptor,
  updateCopilotVersions,
  updateFilterAssetsSelectOption,
  updateFilterByAssets,
  updateFleetInformationMap,
  updateFormattedMapVersions,
  updateHistoricalNotifications,
  updateImportResponse,
  updateLinkedDriverMap,
  updateLinkedVehicleMap,
  updateLoadingAssetsStatus,
  updateOffset,
  updateQuery,
  updateSamsaraInviteStatus,
  updateSelectAllBulkActionStatus,
  updateSelectAllClientOnly,
  updateSelectAllJobTraceId,
  updateSelectedAssets,
  updateSingleAssetDriverData,
  updateSmsStatusesMap,
  updateSort,
} from './assets.repository';

const TRACKJS_SOURCE = 'asset effects';
const NO_STATUS_DETAILS_ERROR_CODE = -4007;
const FLEETPORTAL = 'fleetportal';
const SAMSARA = 'samsara';

const cancelAssetDetails$ = new Subject<void>();

function getAssets(params: UserPagedCollectionParams) {
  cancelAssetDetails$.next();
  const { filterByAssetIds } = assetStoreSnapshot();
  if (filterByAssetIds.length > 0) {
    return AccountsApi.postUsersFleetInfo(params, filterByAssetIds);
  }

  return AccountsApi.getUsersFleetInfo(params);
}

function retrieveLinkedData(deviceIds: string[]) {
  if (partnerSettingsSnapshot().integrationType === 'DriverId') {
    subscribeOnce(
      FleetApi.postDriversVehicleLinks(deviceIds).pipe(
        takeUntil(cancelAssetDetails$),
        trackAssetsRequestsStatus('linked-data'),
        tapResult(({ data }) => {
          updateLinkedVehicleMap(data.driverVehicleLinks);
        })
      )
    );
  }
  if (partnerSettingsSnapshot().integrationType === 'VehicleId') {
    subscribeOnce(
      FleetApi.postVehiclesDriverLinks(deviceIds).pipe(
        takeUntil(cancelAssetDetails$),
        trackAssetsRequestsStatus('linked-data'),
        tapResult(({ data }) => {
          updateLinkedDriverMap(data.vehicleDriverLinkDetails);
        })
      )
    );
  }
}

function retrieveSmsInformation(
  assets: LicenseUser[],
  size: number,
  hasAssets: boolean
) {
  const { accountId } = getCurrentAuthenticatedUser();
  const deviceIds = assets.map((asset) => asset.externalId ?? '');

  subscribeOnce(
    SmsNotificationServiceApi.postSmsNotificationStatusDetails(
      accountId,
      deviceIds
    ).pipe(
      takeUntil(cancelAssetDetails$),
      trackAssetsRequestsStatus('sms-information'),
      catchError((response: NormalizedResponse) => {
        const { error } = response;
        if (
          !Array.isArray(error) &&
          error['errorCode'] === NO_STATUS_DETAILS_ERROR_CODE
        ) {
          return of([]);
        }
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw error;
      })
    ),
    {
      next: (smsStatuses) => {
        updateSmsStatusesMap(smsStatuses);
        updateAssets(
          mapSmsStatusesToAssets(smsStatuses, assets),
          size,
          hasAssets
        );
        updateLoadingAssetsStatus(false);
      },
      error: (error: NormalizedResponse) => {
        updateToast({
          type: 'danger',
          content: translate('assets.assets.errorLoadingSMSStatusInformation'),
        });

        reportToTrackjs(
          TRACKJS_SOURCE,
          'Error loading sms status information',
          getErrorContext(error)
        );
      },
    }
  );
}

function loadAdditionalAssetData(
  loadLinkedData: boolean,
  loadSmsData: boolean,
  assets: LicenseUser[],
  size: number,
  hasAssets: boolean
) {
  const deviceIds = assets.map((asset) => asset.externalId ?? '');
  if (loadLinkedData) {
    retrieveLinkedData(deviceIds);
  }
  if (loadSmsData) {
    retrieveSmsInformation(assets, size, hasAssets);
  }

  subscribeOnce(
    combineLatest([
      fleetInformationStatus$,
      linkedDataStatus$,
      smsInformationStatus$,
    ]).pipe(
      filter(
        ([fleetStatus, linkedStatus, smsStatus]) =>
          fleetStatus.value !== 'pending' &&
          linkedStatus.value !== 'pending' &&
          smsStatus.value !== 'pending'
      ),
      take(1)
    ),
    () => {
      updateLoadingAssetsStatus(false);
    }
  );
}

const loadAssets$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadAssets),
    tapResult(() => {
      updateLoadingAssetsStatus(true);

      const {
        offset,
        query,
        filterAssetsSelectOption,
        sortDirection,
        sortExpression,
      } = assetStoreSnapshot();

      let forcedSortDirection: '' | 'asc' | 'desc' = 'asc';
      let forcedSortExpression = 'externalId';
      if (sortDirection !== '' && sortExpression !== '') {
        forcedSortDirection = sortDirection;
        forcedSortExpression = sortExpression;
      }

      const params = new UserPagedCollectionParams({
        offset,
        query,
        sortDirection: forcedSortDirection,
        sortExpression: forcedSortExpression,
        limit: dataTablePageSizeSnapshot(),
      });
      setFilterOptions(params, filterAssetsSelectOption);

      subscribeOnce(
        getAssets(params),
        ({ data, unfilteredSize, size }) => {
          const assets = createLicenseUsers(data, accountSummarySnapshot());
          const hasAssets = unfilteredSize > 0;
          updateAssets(assets, size, hasAssets);

          dispatch(
            setBrazeAttribute({
              key: 'account_num_assets',
              value: unfilteredSize,
            })
          );

          const assetsAsFleetData = data.map((asset) => {
            const fleetData = {
              ...asset,
            } as unknown as FleetVehicleInformation;
            fleetData.deviceId = asset.externalId;
            return fleetData;
          });
          updateFleetInformationMap(assetsAsFleetData);

          const loadLinkedData = linkedDataEnabled() && size > 0;
          const loadSmsData = companySettingsSnapshot().allowSMS && size > 0;

          if (loadLinkedData || loadSmsData) {
            loadAdditionalAssetData(
              loadLinkedData,
              loadSmsData,
              assets,
              size,
              hasAssets
            );
          } else {
            updateLoadingAssetsStatus(false);
          }
        },
        (error) => {
          if (error instanceof Response) {
            updateToast({
              type: 'danger',
              content: translate('assets.assets.errorLoadingAssets'),
            });
          }
          reportToTrackjs(
            TRACKJS_SOURCE,
            'Error loading assets',
            getErrorContext(error)
          );
          updateLoadingAssetsStatus(false);
        }
      );
    })
  )
);

const loadFleetData$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadFleetData),
    tapResult(({ externalIds }) => {
      subscribeOnce(
        FleetApi.postCompanyDevicesInformation(externalIds).pipe(
          takeUntil(cancelAssetDetails$),
          trackAssetsRequestsStatus('fleet-information')
        ),
        (fleetInformation) => {
          updateFleetInformationMap(fleetInformation.data);
        },
        (error) => {
          if (error instanceof Response) {
            updateToast({
              type: 'danger',
              content: translate('assets.assets.errorLoadingFleetInformation'),
            });
            reportToTrackjs(
              TRACKJS_SOURCE,
              'Error loading fleet information',
              getErrorContext(error)
            );
          }
        }
      );
    })
  )
);

const loadSingleAssetDriverData$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadSingleAssetDriverData),
    tapResult(({ vehicleId }) => {
      subscribeOnce(
        FleetApi.postVehiclesDriverLinks([vehicleId]).pipe(
          takeUntil(cancelAssetDetails$),
          trackAssetsRequestsStatus('single-asset-driver-data'),
          tapResult(({ data }) => {
            const driverData =
              data.vehicleDriverLinkDetails.length > 0
                ? data.vehicleDriverLinkDetails[0]
                : null;
            updateSingleAssetDriverData(driverData);
          })
        )
      );
    })
  )
);

const reloadAssets$ = createEffect((actions) =>
  actions.pipe(
    ofType(reloadAssets),
    tapResult(() => {
      dispatch(setAssetsOffset({ offset: 0 }));
      dispatch(loadAssets());
    })
  )
);

const setAssetsOffset$ = createEffect((actions) =>
  actions.pipe(
    ofType(setAssetsOffset),
    tapResult(({ offset }) => {
      updateOffset(offset);
    })
  )
);

const setAssetsSort$ = createEffect((actions) =>
  actions.pipe(
    ofType(setAssetsSort),
    tapResult(({ sortExpression, sortDirection }) => {
      updateSort(sortExpression, sortDirection);
    })
  )
);

const setAssetsQuery$ = createEffect((actions) =>
  actions.pipe(
    ofType(setAssetsQuery),
    tapResult(({ query }) => {
      const { selectAllClientOnlyValue, selectAllClientOnlyScope } =
        assetStoreSnapshot();
      if (
        selectAllClientOnlyValue === 'all' &&
        selectAllClientOnlyScope === 'dataset'
      ) {
        updateSelectAllClientOnly('none', 'page');
      }

      updateQuery(query);
    })
  )
);

const setFilterAssetsSelectOption$ = createEffect((actions) =>
  actions.pipe(
    ofType(setFilterAssetsSelectOption),
    tapResult(({ filterAssetsSelectOption }) => {
      const { selectAllClientOnlyValue, selectAllClientOnlyScope } =
        assetStoreSnapshot();
      if (
        selectAllClientOnlyValue === 'all' &&
        selectAllClientOnlyScope === 'dataset'
      ) {
        updateSelectAllClientOnly('none', 'page');
      }

      updateFilterAssetsSelectOption(filterAssetsSelectOption);
    })
  )
);

const setSelectAsset$ = createEffect((actions) =>
  actions.pipe(
    ofType(selectAsset),
    map((props) => {
      let checked = props.checked;
      const {
        selectAllClientOnlyValue: value,
        selectAllClientOnlyScope: scope,
        selection,
      } = assetStoreSnapshot();
      if (value === 'all' && scope === 'dataset') {
        checked = false;

        selection.clear();
        assetStoreEntities().forEach((asset) => {
          selection.set(asset.externalId ?? '', asset);
        });

        updateSelectAllClientOnly('all', 'page');
      }
      return { checked, asset: props.asset };
    }),
    tapResult(({ checked, asset }) => {
      const { selection } = assetStoreSnapshot();
      if (checked) {
        selection.set(asset.externalId ?? '', asset);
      } else {
        selection.delete(asset.externalId ?? '');
      }
      updateSelectedAssets();
      dispatch(refreshAssetsRowsDescriptor());
    })
  )
);

const selectAllAssets$ = createEffect((actions) =>
  actions.pipe(
    ofType(selectAllAssets),
    tapResult(({ value, scope }) => {
      updateSelectAllClientOnly(value, scope);

      const { selection } = assetStoreSnapshot();
      if (value === 'all' && scope === 'page') {
        assetStoreEntities().forEach((asset) => {
          selection.set(asset.externalId ?? '', asset);
        });
      } else if (value === 'none' && scope === 'page') {
        assetStoreEntities().forEach((asset) => {
          selection.delete(asset.externalId ?? '');
        });
      } else {
        selection.clear();
      }

      updateSelectedAssets();

      dispatch(refreshAssetsRowsDescriptor());
    })
  )
);

const setAssetsRowsDescriptor$ = createEffect((actions) =>
  actions.pipe(
    ofType(refreshAssetsRowsDescriptor),
    tapResult(() => {
      if (loadingSnapshot()) {
        updateAssetsRowsDescriptor(new LoadingRowsDescriptor());
        return;
      }

      const {
        totalAssets: totalCount,
        selection,
        offset,
        selectAllClientOnlyValue,
        selectAllClientOnlyScope,
      } = assetStoreSnapshot();

      if (totalCount === 0) {
        updateAssetsRowsDescriptor(EMPTY_ROWS_DESCRIPTOR);
        return;
      }

      const allDataSelected =
        selectAllClientOnlyValue === 'all' &&
        selectAllClientOnlyScope === 'dataset';

      const rows = assetStoreEntities().map(
        (asset) =>
          new RowDescriptor(asset, (row) => row.data.id, {
            isSelected:
              allDataSelected || selection.has(asset.externalId ?? ''),
          })
      );

      const totalSelectedOnPage = rows.filter((row) => row.isSelected).length;
      const totalSelected = allDataSelected ? totalCount : selection.size;

      const selectAllState = SelectAllState.Deselected;
      const disabled = false;

      const selectAllValue: ClientOnlyValue =
        allDataSelected || totalSelectedOnPage === rows.length
          ? 'all'
          : totalSelectedOnPage === 0
            ? 'none'
            : 'some';

      updateAssetsRowsDescriptor(
        new UpdateRowsDescriptor(
          rows,
          offset,
          totalCount,
          totalSelected,
          selectAllState,
          disabled,
          {
            value: selectAllValue,
            scope: allDataSelected ? 'dataset' : 'page',
          }
        )
      );
    })
  )
);

const clearSelectedAssets$ = createEffect((actions) =>
  actions.pipe(
    ofType(clearSelectedAssets),
    tapResult(() => {
      const { selection } = assetStoreSnapshot();
      selection.clear();
      updateSelectedAssets();
      dispatch(refreshAssetsRowsDescriptor());
    })
  )
);

const clearSingleAssetDriverData$ = createEffect((actions) =>
  actions.pipe(
    ofType(clearSingleAssetDriverData),
    tapResult(() => {
      updateSingleAssetDriverData(null);
    })
  )
);

const loadUsersCsv$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadUsersCsv),
    switchMap(() => {
      return AccountsApi.getUsersCsvfile().pipe(
        trackAssetsRequests(['fetch-csv']),
        tapResult(
          (response) => {
            downloadFile(response, 'Assets.csv');
            updateToast({
              type: 'success',
              content: translate('assets.assets.exportedAssets'),
            });
          },
          (error) => {
            updateToast({
              type: 'danger',
              content: translate('assets.assets.failedToExportAssets'),
            });
            reportToTrackjs(
              TRACKJS_SOURCE,
              'Error exporting assets',
              getErrorContext(error)
            );
          }
        )
      );
    })
  )
);

const clearAssets$ = createEffect((actions) =>
  actions.pipe(
    ofType(clearAssets),
    tapResult(() => {
      clearAssetsStore();
    })
  )
);

const setFilterByAssetIds$ = createEffect((actions) =>
  actions.pipe(
    ofType(setFilterByAssetIds),
    tapResult(({ filterByAssetIds }) => {
      updateFilterByAssets(filterByAssetIds);
    })
  )
);

const loadHistoricalNotifications$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadHistoricalNotifications),
    switchMap(({ externalId }) => {
      return CopilotMessagePublisherApi.getUsersMessages(externalId).pipe(
        trackAssetsRequestsStatus('load-historical-notifications'),
        tapResult(
          (historicalNotifications) => {
            updateHistoricalNotifications(
              historicalNotifications
                .filter(({ messageType }) => messageType === 'custom_display')
                .map((notification) => new HistoricalNotification(notification))
            );
          },
          () => {
            updateHistoricalNotifications([], true);
          }
        )
      );
    })
  )
);

function createSamsaraInvites(
  asset: LicenseUser,
  driverId: string,
  product: LicenseProduct
) {
  const messages: CpihMessage[] = [];
  if (isCoPilotLicense(product)) {
    messages.push({
      driverId,
      message: translate('assets.assets.samsaraInviteCoPilot', {
        link: generateCopilotQuickLoginLink(asset.externalId ?? ''),
      }),
    });
  } else {
    messages.push({
      driverId,
      message: translate('assets.assets.samsaraInviteMileOn'),
    });
  }
  return messages;
}

function inviteSamsaraAssets(
  inviteRecords: ProductInvite[],
  messages: CpihMessage[]
) {
  subscribeOnce(AccountsApi.postUsersInviteMultiple(inviteRecords), {
    next: () => {
      subscribeOnce(
        CpihMessageExporterApi.postMessageConsumerPartner(
          FLEETPORTAL,
          SAMSARA,
          messages
        ).pipe(trackAssetsRequestsStatus('samsara-invite')),
        {
          complete: () => {
            updateSamsaraInviteStatus();
            updateToast({
              type: 'success',
              content: translate('assets.assets.samsaraInviteSuccess'),
            });
            dispatch(reloadAssets);
          },
          error: (error) => {
            reportToTrackjs(
              TRACKJS_SOURCE,
              'Error exporting assets',
              getErrorContext(error)
            );
            updateToast({
              type: 'danger',
              content: translate('assets.assets.samsaraInviteError'),
            });
          },
        }
      );
    },
  });
}

export const inviteSamsaraUser$ = createEffect((actions) =>
  actions.pipe(
    ofType(inviteSamsaraUser),
    tapResult(({ asset, product }) => {
      const samsaraInvite = {
        id: asset.id ?? 0,
        productId: product.productId ?? 0,
        invitationType: 'Email',
      } as ProductInvite;

      let driverId: string;
      if (partnerSettingsSnapshot().integrationType === 'DriverId') {
        driverId = (
          assetStoreSnapshot().linkedVehicleMap.get(asset.externalId ?? '') ?? {
            integrationDriverId: '',
          }
        ).integrationDriverId;
      } else {
        const mainDriver = determineMainDriver(
          assetStoreSnapshot().linkedDriverMap.get(asset.externalId ?? '') ?? {
            vehicleId: '',
            vehicleDriverLinks: [],
          }
        );
        driverId = mainDriver === null ? '' : mainDriver.integrationDriverId;
      }

      inviteSamsaraAssets(
        [samsaraInvite],
        createSamsaraInvites(asset, driverId, product)
      );
    })
  )
);

export const inviteSamsaraUsers$ = createEffect((actions) =>
  actions.pipe(
    ofType(inviteSamsaraUsers),
    tapResult(({ assets }) => {
      const lookupDriverId = createDriverIdLookup();

      const samsaraInvites: ProductInvite[] = [];
      assets.forEach(({ id, assignments }) => {
        assignments.forEach(({ productId }) => {
          samsaraInvites.push({
            id: id ?? -1,
            productId: productId ?? 0,
            invitationType: 'Email',
          });
        });
      });
      const inviteMap = new Map<string, ProductInvite>(
        samsaraInvites.map((asset) => [`${asset.id}-${asset.productId}`, asset])
      );
      const inviteRecords = Array.from(inviteMap.values());

      let messages: CpihMessage[] = [];

      for (const asset of assets) {
        const driverId = lookupDriverId(asset);

        for (const license of asset.assignments) {
          messages = messages.concat(
            createSamsaraInvites(asset, driverId, license)
          );
        }
      }

      inviteSamsaraAssets(inviteRecords, messages);
    })
  )
);

function completeAssetImport(importResponse: AssetsImportResponse) {
  updateAssetImportStatus();
  updateImportResponse(importResponse);
  if (importResponse.imported > 0) {
    dispatch(reloadAssets());
  }
}

const importAssets$ = createEffect((actions) =>
  actions.pipe(
    ofType(importAssets),
    trackAssetsRequestsStatus('asset-import'),
    tap(() => {
      dispatch(clearImportResponse());
    }),
    chimportAssets(TRACKJS_SOURCE),
    tapResult((importResponse) => {
      const isAzureAccount =
        lookupAccountCloudProvider(
          getCurrentAuthenticatedUser().accountName ?? ''
        ) === 'Azure';
      if (isAzureAccount && isFeatureEnabled('azure-delay')) {
        setTimeout(() => {
          completeAssetImport(importResponse);
        }, TEN_SECONDS_IN_MS);
      } else {
        completeAssetImport(importResponse);
      }
    })
  )
);

const clearImportResponse$ = createEffect((actions) =>
  actions.pipe(
    ofType(clearImportResponse),
    tap(() => {
      updateImportResponse(new AssetsImportResponse());
    })
  )
);

function checkDeletion(time: number, externalId: string) {
  if (time >= TEN_SECONDS_IN_MS) {
    updateAssetDeleted();
    return;
  }
  const startTime = Date.now();

  subscribeOnce(AccountsApi.getUsersFleetInfoAsset(externalId), {
    next: (response) => {
      if (response.data.length === 0) {
        updateAssetDeleted();
      } else {
        const endTime = Date.now();
        const elapsedTime = endTime - startTime;
        checkDeletion(elapsedTime + time, externalId);
      }
    },
  });
}

const checkIfAssetIsDeleted$ = createEffect((actions) =>
  actions.pipe(
    ofType(checkIfAssetIsDeleted),
    tapResult(({ externalId }) => {
      checkDeletion(0, externalId);
    })
  )
);

function checkCreation(time: number, externalId: string) {
  if (time >= TEN_SECONDS_IN_MS) {
    updateAssetCreatedOrUpdated();
    return;
  }
  const startTime = Date.now();

  subscribeOnce(AccountsApi.getUsersFleetInfoAsset(externalId), {
    next: (response) => {
      if (response.data.length === 0) {
        const endTime = Date.now();
        const elapsedTime = endTime - startTime;
        checkCreation(elapsedTime + time, externalId);
      } else {
        updateAssetCreatedOrUpdated();
      }
    },
  });
}

const checkIfAssetIsCreated$ = createEffect((actions) =>
  actions.pipe(
    ofType(checkIfAssetIsCreated),
    tapResult(({ externalId }) => {
      checkCreation(0, externalId);
    })
  )
);

function checkAssignmentsAreEqual(
  formAssignments: LicenseProduct[],
  responseAssignments: LicenseProduct[]
) {
  for (const assignment of formAssignments) {
    if (
      !responseAssignments.some((res) => res.productId === assignment.productId)
    ) {
      return false;
    }
  }

  return true;
}

function checkAssetChanged(
  time: number,
  externalId: string,
  asset: LicenseUser
) {
  if (time >= TEN_SECONDS_IN_MS) {
    updateAssetCreatedOrUpdated();
    return;
  }
  const startTime = Date.now();

  subscribeOnce(AccountsApi.getUsersFleetInfoAsset(externalId), {
    next: (response) => {
      let updated = false;
      const assets = createLicenseUsers(
        response.data,
        accountSummarySnapshot()
      );
      const newAsset = assets[0];

      if (
        asset.firstName === newAsset.firstName &&
        asset.lastName === newAsset.lastName &&
        asset.email === newAsset.email &&
        asset.phoneNumber === newAsset.phoneNumber &&
        asset.externalId === newAsset.externalId &&
        asset.id === newAsset.id &&
        checkAssignmentsAreEqual(asset.assignments, newAsset.assignments)
      ) {
        updated = true;
      }

      if (updated) {
        updateAssetCreatedOrUpdated();
      } else {
        const endTime = Date.now();
        const elapsedTime = endTime - startTime;
        checkAssetChanged(elapsedTime + time, externalId, asset);
      }
    },
  });
}

const checkIfAssetIsChanged$ = createEffect((actions) =>
  actions.pipe(
    ofType(checkIfAssetIsChanged),
    tapResult(({ externalId, asset }) => {
      checkAssetChanged(0, externalId, asset);
    })
  )
);

const loadCopilotVersions$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadCopilotVersions),
    switchMap(() =>
      AccountsApi.getCompanyAppversionV2().pipe(
        catchError(() => of([])),
        map((values) => values.map((item) => item.appVersion ?? ''))
      )
    ),
    tapResult(updateCopilotVersions)
  )
);

const loadMapDataVersions$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadMapDataVersions),
    switchMap(() =>
      AccountsApi.getCompanyMapDataVersion().pipe(
        catchError(() => of([])),
        map((values) => {
          const formattedMapVersions = new Map<string, string>();
          values.forEach(({ mapDataVersion, formattedDataVersion }) => {
            formattedMapVersions.set(mapDataVersion, formattedDataVersion);
          });
          return formattedMapVersions;
        })
      )
    ),
    tapResult(updateFormattedMapVersions)
  )
);

const selectAllBulkAssign$ = createEffect((actions) =>
  actions.pipe(
    ofType(selectAllBulkAssign),
    tapResult(({ productId, addons }) => {
      updateSelectAllBulkActionStatus('pending');

      const { offset, query, filterAssetsSelectOption } = assetStoreSnapshot();

      const params = new UserPagedCollectionParams({
        offset,
        query,
        limit: dataTablePageSizeSnapshot(),
      });
      setFilterOptions(params, filterAssetsSelectOption);

      const { filterByAssetIds } = assetStoreSnapshot();

      subscribeOnce(
        AccountsApi.postBulkAssetLicense(
          'Assign',
          params,
          productId,
          addons,
          filterByAssetIds
        ),
        ({ jobTraceId }) => {
          updateSelectAllJobTraceId(jobTraceId);
          updateSelectAllBulkActionStatus('success');
        },
        (error) => {
          const message = translate(
            'assets.selectAllBulkAction.errorBulkAssigningAll'
          );
          reportToTrackjs(TRACKJS_SOURCE, message, getErrorContext(error));
          updateSelectAllBulkActionStatus('error', message);
        }
      );
    })
  )
);

const selectAllBulkUnassign$ = createEffect((actions) =>
  actions.pipe(
    ofType(selectAllBulkUnassign),
    tapResult(({ productId, addons }) => {
      updateSelectAllBulkActionStatus('pending');

      const { offset, query, filterAssetsSelectOption } = assetStoreSnapshot();

      const params = new UserPagedCollectionParams({
        offset,
        query,
        limit: dataTablePageSizeSnapshot(),
      });
      setFilterOptions(params, filterAssetsSelectOption);

      const { filterByAssetIds } = assetStoreSnapshot();

      subscribeOnce(
        AccountsApi.postBulkAssetLicense(
          'Unassign',
          params,
          productId,
          addons,
          filterByAssetIds
        ),
        ({ jobTraceId }) => {
          updateSelectAllJobTraceId(jobTraceId);
          updateSelectAllBulkActionStatus('success');
        },
        (error) => {
          const message = translate(
            'assets.selectAllBulkAction.errorBulkUnassigningAll'
          );
          reportToTrackjs(TRACKJS_SOURCE, message, getErrorContext(error));
          updateSelectAllBulkActionStatus('error', message);
        }
      );
    })
  )
);

const selectAllBulkInvite$ = createEffect((actions) =>
  actions.pipe(
    ofType(selectAllBulkInvite),
    tapResult(({ samsaraMessageEnabled, inviteLanguage }) => {
      updateSelectAllBulkActionStatus('pending');

      const { offset, query, filterAssetsSelectOption } = assetStoreSnapshot();

      const params = new UserPagedCollectionParams({
        offset,
        query,
        limit: dataTablePageSizeSnapshot(),
      });
      setFilterOptions(params, filterAssetsSelectOption);

      const { filterByAssetIds } = assetStoreSnapshot();

      subscribeOnce(
        AccountsApi.postBulkAssetInvite(
          params,
          samsaraMessageEnabled,
          inviteLanguage,
          filterByAssetIds
        ),
        ({ jobTraceId }) => {
          updateSelectAllJobTraceId(jobTraceId);
          updateSelectAllBulkActionStatus('success');
        },
        (error) => {
          const message = translate(
            'assets.selectAllBulkAction.errorBulkInvitingAll'
          );
          reportToTrackjs(TRACKJS_SOURCE, message, getErrorContext(error));
          updateSelectAllBulkActionStatus('error', message);
        }
      );
    })
  )
);

const selectAllBulkNotify$ = createEffect((actions) =>
  actions.pipe(
    ofType(selectAllBulkNotify),
    tapResult(({ message }) => {
      updateSelectAllBulkActionStatus('pending');

      const { offset, query, filterAssetsSelectOption } = assetStoreSnapshot();

      const params = new UserPagedCollectionParams({
        offset,
        query,
        limit: dataTablePageSizeSnapshot(),
      });
      setFilterOptions(params, filterAssetsSelectOption);

      const { filterByAssetIds } = assetStoreSnapshot();

      subscribeOnce(
        AccountsApi.postBulkNotifyAsset(params, message, filterByAssetIds),
        ({ jobTraceId }) => {
          updateSelectAllJobTraceId(jobTraceId);
          updateSelectAllBulkActionStatus('success');
        },
        (error) => {
          const message = translate(
            'assets.selectAllBulkAction.errorBulkNotifyingAll'
          );
          reportToTrackjs(TRACKJS_SOURCE, message, getErrorContext(error));
          updateSelectAllBulkActionStatus('error', message);
        }
      );
    })
  )
);

const selectAllBulkResetPassword$ = createEffect((actions) =>
  actions.pipe(
    ofType(selectAllBulkResetPassword),
    tapResult(({ forcePasswordReset, password }) => {
      updateSelectAllBulkActionStatus('pending');

      const { offset, query, filterAssetsSelectOption } = assetStoreSnapshot();

      const params = new UserPagedCollectionParams({
        offset,
        query,
        limit: dataTablePageSizeSnapshot(),
      });
      setFilterOptions(params, filterAssetsSelectOption);

      const { filterByAssetIds } = assetStoreSnapshot();

      subscribeOnce(
        AccountsApi.postBulkAssetPasswordReset(
          params,
          password,
          forcePasswordReset,
          filterByAssetIds
        ),
        ({ jobTraceId }) => {
          updateSelectAllJobTraceId(jobTraceId);
          updateSelectAllBulkActionStatus('success');
        },
        (error) => {
          const message = translate(
            'assets.selectAllBulkAction.errorBulkResettingPasswordAll'
          );
          reportToTrackjs(TRACKJS_SOURCE, message, getErrorContext(error));
          updateSelectAllBulkActionStatus('error', message);
        }
      );
    })
  )
);

const selectAllBulkDelete$ = createEffect((actions) =>
  actions.pipe(
    ofType(selectAllBulkDelete),
    tapResult(() => {
      updateSelectAllBulkActionStatus('pending');

      const { offset, query, filterAssetsSelectOption } = assetStoreSnapshot();

      const params = new UserPagedCollectionParams({
        offset,
        query,
        limit: dataTablePageSizeSnapshot(),
      });
      setFilterOptions(params, filterAssetsSelectOption);

      const { filterByAssetIds } = assetStoreSnapshot();

      subscribeOnce(
        AccountsApi.deleteBulkAssetDelete(params, filterByAssetIds),
        ({ jobTraceId }) => {
          updateSelectAllJobTraceId(jobTraceId);
          updateSelectAllBulkActionStatus('success');
        },
        (error) => {
          const message = translate(
            'assets.selectAllBulkAction.errorBulkDeletingAll'
          );
          reportToTrackjs(TRACKJS_SOURCE, message, getErrorContext(error));
          updateSelectAllBulkActionStatus('error', message);
        }
      );
    })
  )
);

const clearJobTraceId$ = createEffect((actions) =>
  actions.pipe(
    ofType(clearJobTraceId),
    tapResult(() => {
      updateSelectAllJobTraceId(null);
    })
  )
);

const effects = [
  loadAssets$,
  loadFleetData$,
  loadSingleAssetDriverData$,
  reloadAssets$,
  setAssetsOffset$,
  setAssetsSort$,
  setAssetsQuery$,
  setFilterAssetsSelectOption$,
  setSelectAsset$,
  selectAllAssets$,
  setAssetsRowsDescriptor$,
  clearSelectedAssets$,
  clearSingleAssetDriverData$,
  loadUsersCsv$,
  clearAssets$,
  setFilterByAssetIds$,
  loadHistoricalNotifications$,
  inviteSamsaraUser$,
  inviteSamsaraUsers$,
  importAssets$,
  clearImportResponse$,
  checkIfAssetIsDeleted$,
  checkIfAssetIsCreated$,
  checkIfAssetIsChanged$,
  loadCopilotVersions$,
  loadMapDataVersions$,
  selectAllBulkAssign$,
  selectAllBulkUnassign$,
  selectAllBulkInvite$,
  selectAllBulkNotify$,
  selectAllBulkResetPassword$,
  selectAllBulkDelete$,
  clearJobTraceId$,
];

export function registerAssetsEffects() {
  registerEffects(effects);
}

export function deregisterAssetsEffects() {
  removeEffects(effects);
}
